import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Mail, Trophy, Calendar, CheckCircle, Phone, ArrowRight } from "lucide-react";
import axios from "axios";
import { API } from "../config/constants";

export default function ContactPage() {
  const [formType, setFormType] = useState("general");
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "", event_name: "", expected_attendees: "", medal_quantity: "" });
  // Honeypot: hidden from real users, but bots fill every field they find.
  const [honeypot, setHoneypot] = useState("");
  const [callbackHoneypot, setCallbackHoneypot] = useState("");
  // Time trap: when the form rendered. Submissions faster than a human could
  // read the labels are dropped server-side.
  const formLoadedAt = useRef(Date.now());
  const callbackLoadedAt = useRef(Date.now());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Callback request state
  const [showCallback, setShowCallback] = useState(false);
  const [callbackData, setCallbackData] = useState({ name: "", phone: "", email: "", service: "medals", preferred_time: "", message: "" });
  const [callbackSubmitting, setCallbackSubmitting] = useState(false);
  const [callbackSubmitted, setCallbackSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...formData, form_type: formType, website: honeypot, form_loaded_at: formLoadedAt.current });
      setSubmitted(true);
    } catch (error) {
      console.error("Error submitting form:", error);
      alert(error?.response?.status === 429
        ? "You've sent several messages recently. Please try again later, or email us at support@lane4.org."
        : "Unable to submit form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCallbackSubmit = async (e) => {
    e.preventDefault();
    setCallbackSubmitting(true);
    try {
      await axios.post(`${API}/callback`, { ...callbackData, website: callbackHoneypot, form_loaded_at: callbackLoadedAt.current });
      setCallbackSubmitted(true);
    } catch (error) {
      console.error("Error submitting callback:", error);
      alert(error?.response?.status === 429
        ? "You've sent several requests recently. Please try again later, or email us at sales@lane4.org."
        : "Unable to submit callback request. Please try again.");
    } finally {
      setCallbackSubmitting(false);
    }
  };

  // The callback form mounts hidden, so start its clock when it is opened.
  useEffect(() => {
    if (showCallback) callbackLoadedAt.current = Date.now();
  }, [showCallback]);

  const formTypes = [
    { id: "general", label: "General Inquiry", icon: <Mail size={20} /> },
    { id: "medal_quote", label: "Medal Quote", icon: <Trophy size={20} /> },
    { id: "event_organizer", label: "Event Partnership", icon: <Calendar size={20} /> },
  ];

  return (
    <div data-testid="contact-page" className="pt-20">
      <section className="py-16 bg-[#0F2137]">
        <div className="container-custom">
          <span className="section-kicker">Get In Touch</span>
          <h1 className="text-5xl font-bold text-white">Contact Us</h1>
          <p className="text-xl text-gray-300 mt-4 max-w-2xl">We're thrilled at the prospect of managing your online ticket sales or crafting personalized medals for your meet.</p>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-bold text-[#0F2137] mb-6">Contact Information</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-[#0F2137] mb-2">Hours</h3>
                  <p className="text-[#495057]">Monday - Friday<br />8am - 9pm EST</p>
                </div>
                <div>
                  <h3 className="font-semibold text-[#0F2137] mb-2">Email</h3>
                  <p className="text-[#495057]">
                    General: <a href="mailto:info@lane4.org" className="text-[#D90429] hover:underline">info@lane4.org</a><br />
                    Sales: <a href="mailto:sales@lane4.org" className="text-[#D90429] hover:underline">sales@lane4.org</a><br />
                    Support: <a href="mailto:support@lane4.org" className="text-[#D90429] hover:underline">support@lane4.org</a>
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-[#0F2137] mb-2">Address</h3>
                  <p className="text-[#495057]">PO Box 14076<br />St Petersburg, FL 33733</p>
                </div>
              </div>

              {/* Request a Callback CTA */}
              <div className="mt-8 p-6 bg-[#F8F9FA] rounded-xl">
                <h3 className="font-bold text-[#0F2137] mb-2 flex items-center gap-2"><Phone size={20} className="text-[#D90429]" /> Prefer a Call?</h3>
                <p className="text-sm text-[#495057] mb-4">Request a callback from our sales team for medal orders or ticketing inquiries.</p>
                <button onClick={() => setShowCallback(!showCallback)} data-testid="request-callback-btn" className="btn-primary text-sm py-2 px-4 w-full flex items-center justify-center gap-2">
                  Request a Callback <ArrowRight size={16} />
                </button>
              </div>
            </div>

            <div className="lg:col-span-2">
              {/* Callback Form */}
              {showCallback && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-8 bg-[#0F2137] p-8 rounded-xl">
                  {callbackSubmitted ? (
                    <div className="text-center py-6">
                      <CheckCircle className="mx-auto text-green-400 mb-4" size={48} />
                      <h3 className="text-xl font-bold text-white mb-2">Callback Requested!</h3>
                      <p className="text-gray-300">Our sales team will reach out to you shortly.</p>
                    </div>
                  ) : (
                    <>
                      <h3 className="text-xl font-bold text-white mb-4">Request a Callback</h3>
                      <form onSubmit={handleCallbackSubmit} data-testid="callback-form" className="space-y-4">
                      {/* Honeypot — hidden from people, irresistible to bots */}
                      <input type="text" name="website" tabIndex="-1" autoComplete="off" aria-hidden="true"
                        value={callbackHoneypot} onChange={e => setCallbackHoneypot(e.target.value)}
                        style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }} />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <input type="text" required placeholder="Your Name *" value={callbackData.name} onChange={(e) => setCallbackData({ ...callbackData, name: e.target.value })} className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400" data-testid="callback-name" />
                          <input type="tel" required placeholder="Phone Number *" value={callbackData.phone} onChange={(e) => setCallbackData({ ...callbackData, phone: e.target.value })} className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400" data-testid="callback-phone" />
                        </div>
                        <input type="email" required placeholder="Email *" value={callbackData.email} onChange={(e) => setCallbackData({ ...callbackData, email: e.target.value })} className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400" data-testid="callback-email" />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <select value={callbackData.service} onChange={(e) => setCallbackData({ ...callbackData, service: e.target.value })} className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white" data-testid="callback-service">
                            <option value="medals" className="text-black">Custom Medals</option>
                            <option value="ticketing" className="text-black">E-Ticketing</option>
                            <option value="events" className="text-black">Event Partnership</option>
                            <option value="merch" className="text-black">Merchandise</option>
                          </select>
                          <input type="text" placeholder="Preferred Call Time" value={callbackData.preferred_time} onChange={(e) => setCallbackData({ ...callbackData, preferred_time: e.target.value })} className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400" data-testid="callback-time" />
                        </div>
                        <textarea placeholder="Brief description of your needs..." rows={3} value={callbackData.message} onChange={(e) => setCallbackData({ ...callbackData, message: e.target.value })} className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 resize-none" data-testid="callback-message" />
                        <button type="submit" disabled={callbackSubmitting} data-testid="submit-callback-btn" className="btn-primary w-full disabled:opacity-50">{callbackSubmitting ? "Submitting..." : "Request Callback"}</button>
                      </form>
                    </>
                  )}
                </motion.div>
              )}

              {/* Contact Form */}
              {submitted ? (
                <div className="bg-[#F8F9FA] p-12 rounded-xl text-center">
                  <CheckCircle className="mx-auto text-green-500 mb-4" size={64} />
                  <h3 className="text-2xl font-bold text-[#0F2137] mb-2">Message Sent!</h3>
                  <p className="text-[#495057]">We'll get back to you as soon as possible.</p>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap gap-4 mb-8">
                    {formTypes.map((type) => (
                      <button key={type.id} onClick={() => setFormType(type.id)} data-testid={`form-type-${type.id}`} className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${formType === type.id ? "bg-[#D90429] text-white" : "bg-[#F8F9FA] text-[#0F2137] hover:bg-gray-200"}`}>
                        {type.icon}{type.label}
                      </button>
                    ))}
                  </div>
                  <form onSubmit={handleSubmit} className="bg-[#F8F9FA] p-8 rounded-xl" data-testid="contact-form">
                      {/* Honeypot — hidden from people, irresistible to bots */}
                      <input type="text" name="website" tabIndex="-1" autoComplete="off" aria-hidden="true"
                        value={honeypot} onChange={e => setHoneypot(e.target.value)}
                        style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }} />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-[#0F2137] mb-2">Your Name *</label>
                        <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-3 bg-white border border-[#E9ECEF] rounded-lg" data-testid="contact-name-input" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#0F2137] mb-2">Email *</label>
                        <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-3 bg-white border border-[#E9ECEF] rounded-lg" data-testid="contact-email-input" />
                      </div>
                    </div>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-[#0F2137] mb-2">Phone (Optional)</label>
                      <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full px-4 py-3 bg-white border border-[#E9ECEF] rounded-lg" data-testid="contact-phone-input" />
                    </div>
                    {formType === "event_organizer" && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                          <label className="block text-sm font-medium text-[#0F2137] mb-2">Event Name</label>
                          <input type="text" value={formData.event_name} onChange={(e) => setFormData({ ...formData, event_name: e.target.value })} className="w-full px-4 py-3 bg-white border border-[#E9ECEF] rounded-lg" data-testid="contact-event-name-input" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#0F2137] mb-2">Expected Attendees</label>
                          <input type="text" value={formData.expected_attendees} onChange={(e) => setFormData({ ...formData, expected_attendees: e.target.value })} className="w-full px-4 py-3 bg-white border border-[#E9ECEF] rounded-lg" data-testid="contact-attendees-input" />
                        </div>
                      </div>
                    )}
                    {formType === "medal_quote" && (
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-[#0F2137] mb-2">Estimated Quantity</label>
                        <input type="text" value={formData.medal_quantity} onChange={(e) => setFormData({ ...formData, medal_quantity: e.target.value })} placeholder="e.g., 100-200 medals" className="w-full px-4 py-3 bg-white border border-[#E9ECEF] rounded-lg" data-testid="contact-quantity-input" />
                      </div>
                    )}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-[#0F2137] mb-2">Message *</label>
                      <textarea required rows={5} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} className="w-full px-4 py-3 bg-white border border-[#E9ECEF] rounded-lg resize-none" data-testid="contact-message-input" />
                    </div>
                    <button type="submit" disabled={isSubmitting} data-testid="submit-contact-btn" className="btn-primary w-full md:w-auto disabled:opacity-50">{isSubmitting ? "Sending..." : "Send Message"}</button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
